import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ExternalLink, Github, ArrowUpRight, RefreshCw, Filter } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { Link } from "react-router";
import { supabase } from "@/integrations/supabase/client";
import { initialProjects } from "../data/initial-data";

export interface Project {
  id: string;
  title: string;
  category: string;
  description: string;
  full_description: string;
  image_url: string;
  tags: string[];
  tech_stack: string[];
  challenges: string[];
  link_url: string;
  github_url: string;
  is_visible: boolean;
}

export function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { data } = await supabase
          .from('kv_store_5f13ee94')
          .select('value')
          .like('key', 'project:%');
        
        if (data && data.length > 0) {
          const dbProjects = data.map(d => d.value as Project).filter(p => p.is_visible);
          setProjects(dbProjects.length > 0 ? dbProjects : initialProjects);
        } else {
          setProjects(initialProjects);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        setProjects(initialProjects);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const categories = useMemo(() => {
    const cats = ["All", ...new Set(projects.map(p => p.category))];
    return cats;
  }, [projects]);

  const filteredProjects = useMemo(() => {
    if (activeCategory === "All") return projects;
    return projects.filter(p => p.category === activeCategory);
  }, [projects, activeCategory]);

  if (isLoading) return <div className="py-20 text-center"><RefreshCw className="animate-spin mx-auto text-violet-500" /></div>;

  return (
    <div className="space-y-12">
      {/* Category Filter */}
      <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4">
        <div className="flex items-center gap-2 text-neutral-500 mr-4">
          <Filter size={16} />
          <span className="text-[10px] font-black uppercase tracking-widest">Filter:</span>
        </div>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border ${
              activeCategory === cat 
                ? "bg-violet-600 border-violet-500 text-white shadow-[0_0_20px_rgba(139,92,246,0.3)]" 
                : "bg-neutral-900 border-neutral-800 text-neutral-500 hover:border-neutral-700 hover:text-neutral-300"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <AnimatePresence mode="popLayout">
          {filteredProjects.map((project, index) => (
            <motion.div
              layout
              key={project.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="group relative bg-neutral-900/40 backdrop-blur-sm border border-neutral-800/50 rounded-[2rem] overflow-hidden hover:border-white/20 transition-all duration-700 shadow-2xl"
            >
              <div className="aspect-[16/10] overflow-hidden relative">
                <ImageWithFallback
                  src={project.image_url}
                  alt={project.title}
                  className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-neutral-950/40 group-hover:bg-neutral-950/0 transition-colors duration-700" />
                <div className="absolute top-6 left-6 z-20">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] px-4 py-2 bg-black/60 backdrop-blur-md border border-white/10 text-violet-400 rounded-full shadow-xl">
                    {project.category}
                  </span>
                </div>
              </div>

              <div className="p-8 md:p-10 relative z-10">
                <Link 
                  to={`/projects/${project.id}`}
                  className="text-2xl md:text-3xl font-black mb-4 flex items-center justify-between group-hover:text-white transition-colors uppercase tracking-tight leading-tight"
                >
                  {project.title}
                  <div className="w-12 h-12 rounded-full border border-neutral-800 flex items-center justify-center group-hover:border-white/20 group-hover:bg-white/10 transition-all duration-500">
                    <ArrowUpRight className="text-neutral-500 group-hover:text-white transition-all -translate-x-1 group-hover:translate-x-0 group-hover:-translate-y-1" size={20} />
                  </div>
                </Link>
                <p className="text-neutral-500 text-sm md:text-base mb-8 leading-relaxed line-clamp-2 group-hover:text-neutral-300 transition-colors">
                  {project.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-8">
                  {project.tags?.map((tag) => (
                    <span key={tag} className="text-[9px] px-2.5 py-1.5 bg-neutral-950/50 border border-neutral-800 rounded-lg text-neutral-500 font-bold uppercase tracking-widest group-hover:border-white/10 group-hover:text-neutral-300 transition-all">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-6 border-t border-neutral-800/50 pt-8">
                  <Link 
                    to={`/projects/${project.id}`}
                    className="text-sm font-black uppercase tracking-widest text-neutral-400 hover:text-white transition-colors flex items-center gap-2 group/link"
                  >
                    Study Case <div className="w-1 h-1 rounded-full bg-violet-500 group-hover:scale-150 transition-transform" />
                  </Link>
                  <div className="flex gap-4 ml-auto">
                    {project.github_url && <a href={project.github_url} target="_blank" rel="noreferrer" className="text-neutral-500 hover:text-white transition-colors"><Github size={18} /></a>}
                    {project.link_url && <a href={project.link_url} target="_blank" rel="noreferrer" className="text-neutral-500 hover:text-white transition-colors"><ExternalLink size={18} /></a>}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}